import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'

interface DataTableWidgetProps {
  title: string
  rows?: Array<Record<string, unknown>>
  hasMore?: boolean
  widgetId: string
  dataSource: string
  dataSourceConfig: Record<string, unknown>
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
}

export function DataTableWidget({
  title,
  rows = [],
  hasMore,
  widgetId,
  dataSource,
  dataSourceConfig,
  onLoadMore,
}: DataTableWidgetProps) {
  const [loading, setLoading] = useState(false)
  const headers = rows.length > 0
    ? Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
    : []

  const handleLoadMore = async () => {
    if (!onLoadMore || loading) return
    setLoading(true)
    try {
      await onLoadMore(widgetId, dataSource, dataSourceConfig)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-amber-500 to-orange-500 text-[10px] text-white font-bold">☰</div>
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{title}</p>
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-neutral-400">No data available</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-neutral-200/70">
          <div className="max-h-full overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-amber-50 to-orange-50/50">
                  {headers.map((header) => (
                    <th key={header} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-neutral-600 first:pl-4">
                      {header.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={index}
                    className={`border-t border-neutral-100/80 transition-colors hover:bg-amber-50/30 ${index % 2 === 1 ? 'bg-neutral-50/40' : 'bg-white'}`}
                  >
                    {headers.map((header) => (
                      <td key={header} className="px-4 py-2.5 text-neutral-700 first:pl-4">
                        {String(row[header] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="flex justify-center border-t border-neutral-100/80 bg-white py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loading}
                className="text-xs"
              >
                {loading && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
